/**
 * `BriefRightPanel` — right-side rail on the brief editor surface that owns the
 * (Comments | Info) tab switcher per Figma node 6224:61894.
 *
 * The rail chrome, collapse and resize come from the shared `DetailSidebar`;
 * this component only supplies the two tab bodies. Comments content comes from
 * `BriefCommentsSidebarBody` — the headless version of the standalone sidebar,
 * used here so we don't end up with a card-in-card. Info tab content is the
 * local `BriefInfoTab`.
 *
 * Tab state is lifted to the page so comment/highlight initiation can force the
 * Comments tab even when the panel is already open on Info.
 *
 * Hardcoded data note (Info tab): the owner dropdown options come from
 * `./mockData` because the backend doesn't thread workspace members to the
 * brief surface yet. See `mockData.ts` for the gap details and TODO markers.
 * Real fields (`title`, `createdAt`, `createdBy`, `updatedAt`) are live.
 */
import type { BriefResponseDto } from '@inference.engine/workspace-api-client/web';
import { DetailSidebar, type DetailSidebarView } from '@/shared/ui';
import { BriefCommentsSidebarBody } from '../BriefCommentsSidebar';
import { BriefInfoTab } from './BriefInfoTab';
import { useTranslation } from 'react-i18next';

export interface BriefRightPanelProps {
  brief: BriefResponseDto;
  /** Scope props forwarded to the Comments tab body. */
  projectId: string;
  workspaceId: string;
  platformProjectId?: string | null;
  /** Forwarded to the Comments tab body — see `BriefCommentsSidebarBody`. */
  onSuggestionApplied?: (sectionId: string, content: Record<string, unknown>) => boolean;
  isSaving?: boolean;
  /** Undefined collapses the rail; `DetailSidebar` animates that itself. */
  view: 'comments' | 'info' | undefined;
  width: number;
  onResize: (next: number) => void;
  onResizeEnd: (next: number) => void;
}

export function BriefRightPanel({
  brief,
  projectId,
  workspaceId,
  platformProjectId,
  onSuggestionApplied,
  isSaving,
  view,
  width,
  onResize,
  onResizeEnd,
}: BriefRightPanelProps) {
  const { t } = useTranslation('projects');

  const commentsView: DetailSidebarView = {
    // eslint-disable-next-line i18next/no-literal-string -- sidebar view value
    value: 'comments',
    label: t('projectBriefDetail.briefRightPanel.comments'),
    content: (
      <BriefCommentsSidebarBody
        projectId={projectId}
        briefId={brief.id}
        workspaceId={workspaceId}
        platformProjectId={platformProjectId}
        brief={brief}
        onSuggestionApplied={onSuggestionApplied}
      />
    ),
  };

  const infoView: DetailSidebarView = {
    // eslint-disable-next-line i18next/no-literal-string -- sidebar view value
    value: 'info',
    label: t('projectBriefDetail.briefRightPanel.info'),
    content: (
      <BriefInfoTab brief={brief} platformProjectId={platformProjectId} isSaving={isSaving} />
    ),
  };

  return (
    <DetailSidebar
      dataTestId="brief-right-panel"
      resizeHandleTestId="brief-panel-resize-handle"
      resizeLabel={t('projectBriefDetail.panelResizeHandle.resizeCommentsPanel')}
      view={view}
      width={width}
      onResize={onResize}
      onResizeEnd={onResizeEnd}
      views={[commentsView, infoView]}
    />
  );
}
